/**
 * Trainer party data, keyed by the pokefirered scriptRef of the trainer's
 * NPC. Phase 12 M4: WorldScene's LOS check looks up the trainer here when
 * a battle fires; BattleScene runs the party as the p2 team.
 *
 * Roster pragmatism: cartridge bug-catchers + Liam use a few species we
 * don't have sprites for (Metapod, Kakuna, Sandshrew). Rather than expand
 * the roster table for every trainer, v0.1 substitutes pre-evolutions /
 * already-rostered species. The substitution is documented per trainer.
 *
 * Brock is the exception — no substitution. Onix sprites are fetched
 * (sprite-fetch updated to include dex 95) so the canonical Geodude+Onix
 * boss fight stays intact.
 *
 * Move sets are best-effort defaults. teamFromParty's moves field handles
 * the actual sim build; trainer parties pass moves explicitly so the
 * cartridge's "BROCK's Onix uses Bind" beats land.
 */

export interface TrainerPartyMember {
  species: string;
  level: number;
  moves: string[];
}

export interface TrainerData {
  /** Display name shown above opponent's HP bar in BattleScene. */
  name: string;
  /** Pre-battle line shown after the LOS encounter resolves and before
   *  BattleScene launches. Single page for v0.1 (multi-page if needed). */
  introLine: string;
  /** Post-battle line. Currently unused (BattleScene exits to overworld
   *  on win); keeping the field for M5+ when we add post-battle dialogue. */
  defeatLine: string;
  /** Cartridge canon party; see roster pragmatism note above. */
  party: TrainerPartyMember[];
  /** Pokédex id of the LEAD species — used by BattleScene to preload the
   *  front sprite (the rest are async-loaded as switches happen, but we
   *  preload the lead so battle open is instant). */
  leadDexId: number;
}

export const TRAINERS: Record<string, TrainerData> = {
  // ── Pewter Gym ────────────────────────────────────────────
  PewterCity_Gym_EventScript_Liam: {
    name: 'CAMPER LIAM',
    introLine: "I'm gonna show you how tough my POKEMON are!",
    defeatLine: "Whoa! That was incredible!",
    // Cartridge: Geodude L10 + Sandshrew L11. Sandshrew has no sprite in
    // v0.1; substituted with a 2nd Geodude (same gym vibe, smaller roster).
    party: [
      { species: 'geodude', level: 10, moves: ['tackle', 'defensecurl'] },
      { species: 'geodude', level: 11, moves: ['tackle', 'defensecurl'] },
    ],
    leadDexId: 74,
  },
  PewterCity_Gym_EventScript_Brock: {
    name: 'LEADER BROCK',
    introLine: "I'm BROCK! I'm Pewter's Gym Leader! My rock-hard willpower is evident in even my POKEMON. Bring it on!",
    defeatLine: "I took you for granted, and so I lost. As proof of your victory, here's the BOULDERBADGE.",
    // Cartridge canon — no substitution. Onix added to v0.1 roster.
    party: [
      { species: 'geodude', level: 12, moves: ['tackle', 'defensecurl'] },
      { species: 'onix', level: 14, moves: ['tackle', 'bind', 'rocktomb'] },
    ],
    leadDexId: 74,
  },

  // ── Viridian Forest bug-catchers ──────────────────────────
  ViridianForest_EventScript_Rick: {
    name: 'BUG CATCHER RICK',
    introLine: "Hey! You have POKEMON! Come on! Let's battle 'em!",
    defeatLine: "Huh? I ran out of POKEMON!",
    party: [
      { species: 'weedle', level: 6, moves: ['poisonsting', 'stringshot'] },
      { species: 'caterpie', level: 6, moves: ['tackle', 'stringshot'] },
    ],
    leadDexId: 13,
  },
  ViridianForest_EventScript_Doug: {
    name: 'BUG CATCHER DOUG',
    introLine: "I'm in training, but I'll battle you anyway!",
    defeatLine: "I'll level my POKEMON up more!",
    // Cartridge: Weedle L7 + Kakuna L7 + Weedle L7. Kakuna substituted
    // with Weedle (its pre-evo) — no Kakuna sprite in v0.1 roster.
    party: [
      { species: 'weedle', level: 7, moves: ['poisonsting', 'stringshot'] },
      { species: 'weedle', level: 7, moves: ['poisonsting', 'stringshot'] },
      { species: 'weedle', level: 7, moves: ['poisonsting', 'stringshot'] },
    ],
    leadDexId: 13,
  },
  ViridianForest_EventScript_Sammy: {
    name: 'BUG CATCHER SAMMY',
    introLine: "Yo! Looks like our POKEMON should fight!",
    defeatLine: "You're better than I thought.",
    party: [{ species: 'weedle', level: 9, moves: ['poisonsting', 'stringshot'] }],
    leadDexId: 13,
  },
  ViridianForest_EventScript_Anthony: {
    name: 'BUG CATCHER ANTHONY',
    introLine: "I caught a lot of POKEMON! Wanna see them?",
    defeatLine: "Aww, you're tough!",
    party: [
      { species: 'caterpie', level: 7, moves: ['tackle', 'stringshot'] },
      { species: 'caterpie', level: 8, moves: ['tackle', 'stringshot'] },
    ],
    leadDexId: 10,
  },
  ViridianForest_EventScript_Charlie: {
    name: 'BUG CATCHER CHARLIE',
    introLine: "Are you in the BUG CATCHING CONTEST? Oh wait, you're not!",
    defeatLine: "Oh well, you win.",
    // Cartridge: Metapod L7 + Caterpie L7 + Metapod L7. Metapod substituted
    // with Caterpie.
    party: [
      { species: 'caterpie', level: 7, moves: ['tackle', 'stringshot'] },
      { species: 'caterpie', level: 7, moves: ['tackle', 'stringshot'] },
      { species: 'caterpie', level: 7, moves: ['tackle', 'stringshot'] },
    ],
    leadDexId: 10,
  },

  // ── Pallet Lab rival (H7a) ────────────────────────────────
  //
  // Cartridge picks the opposing-type starter for BLUE:
  //   player Bulbasaur (grass) → rival Charmander (fire)
  //   player Charmander (fire) → rival Squirtle (water)
  //   player Squirtle (water) → rival Bulbasaur (grass)
  //
  // L5 cartridge movesets per starter (the only moves they know at L5):
  //   Bulbasaur: Tackle, Growl
  //   Charmander: Scratch, Growl
  //   Squirtle: Tackle, Tail Whip
  //
  // Synthetic scriptRefs — not in any runtime.json (the rival NPC's
  // runtime scriptRef is _EventScript_Rival for the tap dialogue; the
  // battle is launched from the cinematic handler which picks one of
  // these three refs based on runState.getParty()[0].species).
  PalletTown_ProfessorOaksLab_EventScript_RivalBattle_VsBulbasaur: {
    name: 'RIVAL BLUE',
    introLine: "Wait, RED! Let's check out our POKéMON! Come on, I'll take you on!",
    defeatLine: 'WHAT? Unbelievable! I picked the wrong POKéMON!',
    party: [{ species: 'charmander', level: 5, moves: ['scratch', 'growl'] }],
    leadDexId: 4,
  },
  PalletTown_ProfessorOaksLab_EventScript_RivalBattle_VsCharmander: {
    name: 'RIVAL BLUE',
    introLine: "Wait, RED! Let's check out our POKéMON! Come on, I'll take you on!",
    defeatLine: 'WHAT? Unbelievable! I picked the wrong POKéMON!',
    party: [{ species: 'squirtle', level: 5, moves: ['tackle', 'tailwhip'] }],
    leadDexId: 7,
  },
  PalletTown_ProfessorOaksLab_EventScript_RivalBattle_VsSquirtle: {
    name: 'RIVAL BLUE',
    introLine: "Wait, RED! Let's check out our POKéMON! Come on, I'll take you on!",
    defeatLine: 'WHAT? Unbelievable! I picked the wrong POKéMON!',
    party: [{ species: 'bulbasaur', level: 5, moves: ['tackle', 'growl'] }],
    leadDexId: 1,
  },
};

/** Lookup helper. Returns undefined for non-trainer NPCs. */
export function getTrainer(scriptRef: string): TrainerData | undefined {
  return TRAINERS[scriptRef];
}

/** PR A4 (eng-review decision 6): scriptRefs that BattleScene's
 *  eventHandlers checks on p1 victory to flip the rival-resolved flag.
 *  Battle code imports this Set + tests `if (REFS.has(ref))` after the
 *  existing `markDefeatedTrainer` call. Battle→data import direction
 *  matches the prior Pewter/Forest trainer-LOS path. */
export const RIVAL_BATTLE_REFS: ReadonlySet<string> = new Set([
  'PalletTown_ProfessorOaksLab_EventScript_RivalBattle_VsBulbasaur',
  'PalletTown_ProfessorOaksLab_EventScript_RivalBattle_VsCharmander',
  'PalletTown_ProfessorOaksLab_EventScript_RivalBattle_VsSquirtle',
]);

/** Resolve which Pallet-lab rival battle ref the player should face,
 *  given their starter species. The opposing-type matchup is cartridge
 *  canon (PalletTown_ProfessorOaksLab/scripts.inc). Returns undefined if
 *  the player's starter isn't one of the three Kanto starters (a
 *  defensive case — should never happen in v0.1 since the DraftScene
 *  pool is locked to bulb/char/squirt). */
export function rivalBattleRefForStarter(playerStarter: string): string | undefined {
  switch (playerStarter) {
    case 'bulbasaur':
      return 'PalletTown_ProfessorOaksLab_EventScript_RivalBattle_VsBulbasaur';
    case 'charmander':
      return 'PalletTown_ProfessorOaksLab_EventScript_RivalBattle_VsCharmander';
    case 'squirtle':
      return 'PalletTown_ProfessorOaksLab_EventScript_RivalBattle_VsSquirtle';
    default:
      return undefined;
  }
}

/** PR A3: which starter species the rival picks given the player's
 *  choice (cartridge opposing-type rule), and the x coordinate of that
 *  ball's pedestal in PalletTown_ProfessorOaksLab.runtime.json. Used
 *  by the labPostPick handler to walk the rival to the opposing
 *  pedestal before he says "I'll take this one, then!"
 *
 *  Pedestal layout per runtime.json:
 *    bulbasaur ball at x=8, squirtle at x=9, charmander at x=10
 *    (all at y=4, the row south of Oak's desk).
 */
export interface RivalPick {
  species: string;
  pedestalX: number;
}
export function opposingStarterFor(playerStarter: string): RivalPick | undefined {
  switch (playerStarter) {
    case 'bulbasaur':
      return { species: 'charmander', pedestalX: 10 };
    case 'charmander':
      return { species: 'squirtle', pedestalX: 9 };
    case 'squirtle':
      return { species: 'bulbasaur', pedestalX: 8 };
    default:
      return undefined;
  }
}
